import { useId } from "preact/hooks";
import type { MapStand } from "../shared/types";
import { mapStandSaturation } from "./venueMapAppearance";
import { venueMapLogoLayout } from "./venueMapLogoLayout";
import {
  venueMapBoothFallbackText,
  venueMapBoothTextLayout,
} from "./venueMapBoothLabelLayout";
import type { VenueMapLogoAsset } from "./useVenueMapLogos";

/** Shared SVG lettering for the scene and selected-booth miniature. */
export function VenueMapBoothLabel({
  stand,
  outline,
  logo,
}: {
  stand: MapStand;
  outline?: string;
  logo?: VenueMapLogoAsset;
}) {
  const filterId = `map-logo-${useId().replace(/:/g, "")}`;
  const layout = logo ? venueMapLogoLayout(stand) : undefined;
  const saturation = mapStandSaturation(stand);
  if (layout && logo) {
    return (
      <g data-booth-label-mode={stand.labelMode}>
        {saturation < 1 && (
          <defs>
            <filter id={filterId} colorInterpolationFilters="sRGB">
              <feColorMatrix type="saturate" values={String(saturation)} />
            </filter>
          </defs>
        )}
        <image
          data-booth-logo={stand.id}
          href={logo.url}
          x={layout.logo.x}
          y={layout.logo.y}
          width={layout.logo.width}
          height={layout.logo.height}
          preserveAspectRatio="xMidYMid meet"
          filter={saturation < 1 ? `url(#${filterId})` : undefined}
        />
        <g textAnchor="middle" fontFamily="Arial, sans-serif">
          {layout.text.map((run, index) => (
            <text
              key={index}
              x={run.x}
              y={run.y}
              fontSize={run.size}
              fontWeight={run.weight}
              textAnchor={
                run.align === "left"
                  ? "start"
                  : run.align === "right"
                    ? "end"
                    : "middle"
              }
              transform={
                run.rotation
                  ? `rotate(${run.rotation} ${run.x} ${run.y})`
                  : undefined
              }
              data-booth-number={
                index === 0 && stand.label.showId !== false
                  ? stand.id
                  : undefined
              }
            >
              {run.text}
            </text>
          ))}
        </g>
      </g>
    );
  }
  // Unavailable logos use the same fitted text as a text-only booth.
  const textLayout = venueMapBoothTextLayout(stand);
  if (textLayout) {
    return (
      <g fontFamily="Arial, sans-serif">
        {textLayout.text.map((run, index) => (
          <text
            key={index}
            x={run.x}
            y={run.y}
            fontSize={run.size}
            fontWeight={run.weight}
            textAnchor={
              run.align === "left"
                ? "start"
                : run.align === "right"
                  ? "end"
                  : "middle"
            }
            transform={
              run.rotation
                ? `rotate(${run.rotation} ${run.x} ${run.y})`
                : undefined
            }
            data-booth-number={
              index === 0 && stand.label.showId !== false ? stand.id : undefined
            }
          >
            {run.text}
          </text>
        ))}
      </g>
    );
  }
  // Keep authored lettering when unusual geometry has no usable label area.
  if (
    outline &&
    !stand.mapLabel?.trim() &&
    stand.label.showId !== false &&
    !stand.labelRotation
  )
    return <path d={outline} />;
  return (
    <g textAnchor="middle" fontFamily="Inter, system-ui, sans-serif">
      {venueMapBoothFallbackText(stand).map((run, index) => (
        <text
          key={index}
          x={run.x}
          y={run.y}
          fontSize={run.size}
          fontWeight={run.weight}
          letterSpacing={
            index === 0 && stand.label.showId !== false ? "0.35" : undefined
          }
          transform={
            run.rotation
              ? `rotate(${run.rotation} ${run.x} ${run.y})`
              : undefined
          }
        >
          {run.text}
        </text>
      ))}
    </g>
  );
}
